import { useEffect, useState } from "react";
import { fetchMeetingNotes, postMeetingNote, fetchMeetingSummary } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { ClipboardList, Target, AlertTriangle, Sparkles, CheckCircle2, Heart } from "lucide-react";
import { toast } from "sonner";
import { MeetingTranscriptAction } from "./IntegrationActions";

/**
 * MeetingNotesPanel — shown on approved mentor requests. Both mentor and mentee
 * can add notes; AI synthesizes a shared summary.
 *
 * Props: requestId, currentUserId, mentorId, menteeId
 */
export default function MeetingNotesPanel({ requestId, currentUserId, mentorId, menteeId }) {
  const [notes, setNotes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [keyTakeaways, setKeyTakeaways] = useState("");
  const [nextActions, setNextActions] = useState("");
  const [blockers, setBlockers] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const author = currentUserId === mentorId ? "mentor" : currentUserId === menteeId ? "mentee" : null;

  const reload = async () => {
    setLoading(true);
    try {
      const d = await fetchMeetingNotes(requestId);
      setNotes(d.notes || []);
      setSummary(d.summary || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const handleSubmit = async () => {
    if (!keyTakeaways.trim()) {
      toast.error("Çıkarımlar zorunlu");
      return;
    }
    setSubmitting(true);
    try {
      await postMeetingNote(requestId, {
        author,
        key_takeaways: keyTakeaways,
        next_actions: nextActions,
        blockers,
      });
      toast.success("Notların kaydedildi");
      setKeyTakeaways("");
      setNextActions("");
      setBlockers("");
      await reload();
    } catch {
      toast.error("Kaydedilemedi");
    } finally {
      setSubmitting(false);
    }
  };

  const loadSummary = async () => {
    setSummaryLoading(true);
    try {
      const d = await fetchMeetingSummary(requestId);
      setSummary(d.summary);
    } catch {
      toast.error("Özet alınamadı");
    } finally {
      setSummaryLoading(false);
    }
  };

  const myNote = notes.find((n) => n.author === author);
  const otherNote = notes.find((n) => n.author !== author);
  const bothSubmitted = notes.some((n) => n.author === "mentor") && notes.some((n) => n.author === "mentee");

  if (loading) {
    return <div className="card-surface p-5"><div className="h-3 bg-slate-200/60 rounded animate-pulse w-1/3" /></div>;
  }

  return (
    <div className="rounded-xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/50 via-white to-white p-5 space-y-4" data-testid={`meeting-notes-${requestId}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-300 flex items-center justify-center">
          <ClipboardList className="w-4 h-4 text-emerald-700" />
        </div>
        <div className="flex-1">
          <div className="text-[0.65rem] uppercase tracking-wider text-emerald-700 font-semibold mb-0.5">
            İlk Görüşme Sonrası Notlar
          </div>
          <p className="text-xs text-slate-600">
            {bothSubmitted
              ? "Her iki taraf da not bıraktı — AI ortak özet üretebilir."
              : myNote
                ? "Notların kaydedildi. Karşı taraf eklediğinde AI özet hazır olacak."
                : "Görüşmenin ardından senin tarafından çıkarımları kaydet."}
          </p>
        </div>
      </div>

      {/* My note input or display */}
      {!myNote ? (
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-700 mb-1.5 block">Anahtar Çıkarımlar *</Label>
            <Textarea value={keyTakeaways} onChange={(e) => setKeyTakeaways(e.target.value)} rows={2} placeholder="Görüşmeden net olarak ne aldın?" className="bg-white text-sm resize-none" data-testid={`note-takeaways-${requestId}`} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-700 mb-1.5 block">Sonraki Aksiyonlar</Label>
              <Textarea value={nextActions} onChange={(e) => setNextActions(e.target.value)} rows={2} placeholder="Bir sonraki adımda ne yapacaksın?" className="bg-white text-sm resize-none" />
            </div>
            <div>
              <Label className="text-xs text-slate-700 mb-1.5 block">Engeller</Label>
              <Textarea value={blockers} onChange={(e) => setBlockers(e.target.value)} rows={2} placeholder="Önünde duran engel var mı?" className="bg-white text-sm resize-none" />
            </div>
          </div>
          <Button onClick={handleSubmit} disabled={submitting || !author} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`note-submit-${requestId}`}>
            {submitting ? "Kaydediliyor…" : "Notları kaydet"}
          </Button>
        </div>
      ) : (
        <div className="rounded-lg bg-white border border-emerald-200 p-3 text-sm">
          <div className="text-[0.65rem] uppercase tracking-wider text-emerald-700 mb-1.5 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Senin notların
          </div>
          <div className="space-y-1.5 text-slate-800">
            <div><span className="text-[0.65rem] uppercase tracking-wider text-slate-500">Çıkarımlar:</span> {myNote.key_takeaways}</div>
            {myNote.next_actions && <div><span className="text-[0.65rem] uppercase tracking-wider text-slate-500">Sonraki:</span> {myNote.next_actions}</div>}
            {myNote.blockers && <div><span className="text-[0.65rem] uppercase tracking-wider text-slate-500">Engeller:</span> {myNote.blockers}</div>}
          </div>
        </div>
      )}

      {/* Other side note */}
      {otherNote && (
        <div className="rounded-lg bg-white border border-slate-200 p-3 text-sm">
          <div className="text-[0.65rem] uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1">
            <Avatar className="w-4 h-4"><AvatarFallback className="text-[0.6rem]">{otherNote.author_name?.charAt(0)}</AvatarFallback></Avatar>
            {otherNote.author_name}'in notları
          </div>
          <div className="space-y-1.5 text-slate-800">
            <div><span className="text-[0.65rem] uppercase tracking-wider text-slate-500">Çıkarımlar:</span> {otherNote.key_takeaways}</div>
            {otherNote.next_actions && <div><span className="text-[0.65rem] uppercase tracking-wider text-slate-500">Sonraki:</span> {otherNote.next_actions}</div>}
            {otherNote.blockers && <div><span className="text-[0.65rem] uppercase tracking-wider text-slate-500">Engeller:</span> {otherNote.blockers}</div>}
          </div>
        </div>
      )}

      {/* AI Summary trigger / display */}
      {bothSubmitted && !summary && !summaryLoading && (
        <div className="flex flex-wrap gap-2">
          <Button onClick={loadSummary} variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 flex-1" data-testid={`note-summary-btn-${requestId}`}>
            <Sparkles className="w-4 h-4 mr-2" /> AI ile ortak özet üret
          </Button>
          <MeetingTranscriptAction when="Son mentorluk görüşmesi" />
        </div>
      )}

      {summaryLoading && (
        <div className="rounded-lg bg-emerald-50/50 border border-emerald-200 p-3 text-xs text-emerald-800 italic">
          Plena AI iki tarafın notlarını birleştiriyor…
        </div>
      )}

      {summary && (
        <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50/50 p-4 space-y-3" data-testid={`meeting-summary-${requestId}`}>
          <div className="text-[0.65rem] uppercase tracking-wider text-emerald-800 font-semibold flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> AI Ortak Özet
          </div>
          {summary.alignment && (
            <div className="rounded-lg bg-white border border-emerald-200 px-3 py-2 text-sm text-emerald-900">
              {summary.alignment}
            </div>
          )}
          {summary.shared_takeaways?.length > 0 && (
            <div>
              <div className="text-[0.65rem] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1"><Target className="w-3 h-3 text-emerald-600" /> Ortak Çıkarımlar</div>
              <ul className="space-y-1">
                {summary.shared_takeaways.map((t, i) => (
                  <li key={i} className="text-sm text-slate-800 flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" /> <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {summary.agreed_next_steps?.length > 0 && (
            <div>
              <div className="text-[0.65rem] uppercase tracking-wider text-slate-500 mb-1">Anlaşılan Sonraki Adımlar</div>
              <ol className="space-y-1">
                {summary.agreed_next_steps.map((s, i) => (
                  <li key={i} className="text-sm text-slate-800 flex gap-2">
                    <span className="font-mono text-xs text-emerald-700 font-semibold w-5 flex-shrink-0">{i + 1}.</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          {summary.watch_outs?.length > 0 && (
            <div>
              <div className="text-[0.65rem] uppercase tracking-wider text-amber-700 mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Dikkat Edilecekler</div>
              <ul className="space-y-1">
                {summary.watch_outs.map((w, i) => (
                  <li key={i} className="text-sm text-amber-900 flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 flex-shrink-0" /> <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {summary.encouragement && (
            <div className="flex items-start gap-2 text-xs text-slate-600 italic pt-1">
              <Heart className="w-3.5 h-3.5 text-rose-500 flex-shrink-0 mt-0.5" />
              <span>{summary.encouragement}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
